import React, { useState, useMemo, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Plus, Trash2, Zap, Check, Users, ShieldCheck, ShieldAlert, Search, Filter } from "lucide-react";
import toast from "react-hot-toast";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";

// Structured Symbol Selector Constants & Utility
const EXCHANGES = ["MCX", "NYMEX", "COMEX", "NSE"];
const INSTRUMENTS = ["CRUDEOIL", "GOLD", "SILVER", "NATURALGAS", "USDINR"];
const SYMBOL_TYPES = [
    { value: "FUT", label: "FUT" },
    { value: "CE", label: "CE" },
    { value: "PE", label: "PE" },
];
const MONTHS = [
    { name: "JAN", num: 1, code: "F" },
    { name: "FEB", num: 2, code: "G" },
    { name: "MAR", num: 3, code: "H" },
    { name: "APR", num: 4, code: "J" },
    { name: "MAY", num: 5, code: "K" },
    { name: "JUN", num: 6, code: "M" },
    { name: "JUL", num: 7, code: "N" },
    { name: "AUG", num: 8, code: "Q" },
    { name: "SEP", num: 9, code: "U" },
    { name: "OCT", num: 10, code: "V" },
    { name: "NOV", num: 11, code: "X" },
    { name: "DEC", num: 12, code: "Z" },
];
const YEARS = ["26", "27", "28", "29", "30"];

const COMEX_NYMEX_CODES = {
    CRUDEOIL: { fut: "CL", opt: "LO" },
    GOLD: { fut: "GC", opt: "OG" },
    SILVER: { fut: "SI", opt: "SO" },
    NATURALGAS: { fut: "NG", opt: "LNE" },
    USDINR: { fut: "USD", opt: "USD" },
};

const COMEX_NYMEX_STRIKE_SCALES = {
    CRUDEOIL: 100,    // e.g. 103 -> 10300
    NATURALGAS: 1000, // e.g. 4.7 -> 4700
    GOLD: 1,          // e.g. 2650 -> 2650
    SILVER: 100,      // e.g. 30 -> 3000
    USDINR: 1,
};

const getMonthCode = (month) => {
    const monthUpper = (month || "OCT").toUpperCase();
    const found = MONTHS.find((m) => m.name === monthUpper);
    return found ? found.code : "V";
};

const buildSymbol = ({
    exchange = "MCX",
    instrument = "CRUDEOIL",
    symbolType = "FUT",
    expiryMonth = "OCT",
    expiryYear = "26",
    strike = "",
}) => {
    const exUpper = (exchange || "MCX").toUpperCase();
    const instUpper = (instrument || "CRUDEOIL").toUpperCase();
    const typeUpper = (symbolType || "FUT").toUpperCase();
    const monthUpper = (expiryMonth || "OCT").toUpperCase();
    const yearStr = (expiryYear || "26").toString();

    const isComexNymex = exUpper === "NYMEX" || exUpper === "COMEX";

    if (isComexNymex) {
        const monthCode = getMonthCode(monthUpper);
        const yearSingleDigit = yearStr.slice(-1);
        const yearTwoDigits = yearStr.padStart(2, "0").slice(-2);
        const codes = COMEX_NYMEX_CODES[instUpper] || { fut: instUpper, opt: instUpper };

        if (typeUpper === "FUT") {
            const futYear = instUpper === "NATURALGAS" ? yearTwoDigits : yearSingleDigit;
            return `${exUpper}:${codes.fut}${monthCode}${futYear}`;
        } else {
            const cpLetter = typeUpper === "CE" ? "C" : "P";
            const scale = COMEX_NYMEX_STRIKE_SCALES[instUpper] || 1;
            const strikeNum = Number(strike) || 0;
            const scaledStrike = Math.round(strikeNum * scale);
            return `${exUpper}:${codes.opt}${monthCode}${yearSingleDigit} ${cpLetter}${scaledStrike}`;
        }
    } else {
        const yearTwoDigits = yearStr.padStart(2, "0").slice(-2);

        if (typeUpper === "FUT") {
            return `${exUpper}:${instUpper}${yearTwoDigits}${monthUpper}FUT`;
        } else {
            const strikeClean = strike.toString().trim();
            return `${exUpper}:${instUpper}${yearTwoDigits}${monthUpper}${strikeClean}${typeUpper}`;
        }
    }
};

const LegLtpDisplay = React.memo(({ symbol }) => {
    const symUpper = (symbol || "").trim().toUpperCase();
    const ltp = useLtpStore((state) => (symUpper ? state.ltps[symUpper] : null));
    return <span className="text-gray-400 font-normal ml-1">(LTP: {ltp || "-"})</span>;
});

const RmsArbOrderModal = ({ isOpen, onClose, users = [], onSuccess, initialLegs = null, initialIsVirtual = true }) => {
    const [strategyType, setStrategyType] = useState("cross_exchange_arb");
    const [targetPnl, setTargetPnl] = useState("");
    const [stopLossPnl, setStopLossPnl] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Client Selection State (PledgePlus Style)
    const [selectedClients, setSelectedClients] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [brokerFilter, setBrokerFilter] = useState("ALL");
    const [isAllVirtual, setIsAllVirtual] = useState(initialIsVirtual);

    // Initial 2 legs
    const [legs, setLegs] = useState([
        {
            exchange: "MCX",
            instrument: "CRUDEOIL",
            symbolType: "FUT",
            expiryMonth: "OCT",
            expiryYear: "26",
            strike: "",
            symbol: "MCX:CRUDEOIL26OCTFUT",
            side: "1", // BUY
            qty: "1",
            orderType: "LIMIT",
            limitPrice: "",
            broker: "fyers",
            client_id: "",
            is_virtual: false,
        },
        {
            exchange: "NYMEX",
            instrument: "CRUDEOIL",
            symbolType: "FUT",
            expiryMonth: "NOV",
            expiryYear: "26",
            strike: "",
            symbol: "NYMEX:CLX6",
            side: "-1", // SELL
            qty: "1",
            orderType: "LIMIT",
            limitPrice: "",
            broker: "virtual",
            client_id: "VIRTUAL",
            is_virtual: true,
        }
    ]);

    useEffect(() => {
        if (isOpen) {
            setIsAllVirtual(initialIsVirtual);
            setSelectedClients([]);
            if (initialLegs) {
                setLegs(initialLegs);
            }
        }
    }, [isOpen, initialLegs, initialIsVirtual]);

    const eligibleClients = useMemo(() => {
        if (!users) return [];
        return (users || []).filter(u => u && (u.status || "").toLowerCase() === "active").map(u => ({
            client_id: u.client_id || u.clientId,
            clientId: u.client_id || u.clientId,
            name: u.name || u.client_id || u.clientId,
            broker: u.broker || "fyers",
            label: u.label || "",
        }));
    }, [users]);

    const uniqueLabels = useMemo(() => {
        const labels = new Set();
        eligibleClients.forEach(c => {
            if (c.label) labels.add(c.label);
        });
        return Array.from(labels).sort();
    }, [eligibleClients]);

    const filteredClients = useMemo(() => {
        let list = eligibleClients;
        if (brokerFilter !== "ALL") {
            list = list.filter(c => (c.broker || "").toLowerCase() === brokerFilter.toLowerCase());
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(c =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.clientId || c.client_id || "").toLowerCase().includes(q) ||
                (c.label || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [eligibleClients, brokerFilter, searchQuery]);

    if (!isOpen) return null;

    const toggleClientSelection = (cid) => {
        setSelectedClients(prev =>
            prev.includes(cid) ? prev.filter(c => c !== cid) : [...prev, cid]
        );
    };

    const toggleSelectAll = () => {
        const visibleCids = filteredClients.map(c => c.clientId).filter(Boolean);
        const allSelected = visibleCids.every(id => selectedClients.includes(id));

        if (allSelected) {
            setSelectedClients(prev => prev.filter(id => !visibleCids.includes(id)));
        } else {
            const newSet = new Set([...selectedClients, ...visibleCids]);
            setSelectedClients(Array.from(newSet));
        }
    };

    const toggleLabelSelection = (label) => {
        const labelClients = filteredClients.filter(c => c.label === label);
        if (labelClients.length === 0) return;

        const labelCids = labelClients.map(c => c.clientId);
        const allSelected = labelCids.every(id => selectedClients.includes(id));

        if (allSelected) {
            setSelectedClients(prev => prev.filter(id => !labelCids.includes(id)));
        } else {
            const newSet = new Set([...selectedClients, ...labelCids]);
            setSelectedClients(Array.from(newSet));
        }
    };

    const handleAddLeg = () => {
        const defaultExchange = "MCX";
        const defaultInst = "CRUDEOIL";
        const defaultType = "FUT";
        const defaultMonth = "OCT";
        const defaultYear = "26";
        const defaultStrike = "";

        const generatedSym = buildSymbol({
            exchange: defaultExchange,
            instrument: defaultInst,
            symbolType: defaultType,
            expiryMonth: defaultMonth,
            expiryYear: defaultYear,
            strike: defaultStrike,
        });

        setLegs((prev) => [
            ...prev,
            {
                exchange: defaultExchange,
                instrument: defaultInst,
                symbolType: defaultType,
                expiryMonth: defaultMonth,
                expiryYear: defaultYear,
                strike: defaultStrike,
                symbol: generatedSym,
                side: "1",
                qty: "1",
                orderType: "LIMIT",
                limitPrice: "",
                broker: isAllVirtual ? "virtual" : "fyers",
                client_id: isAllVirtual ? "VIRTUAL" : (selectedClients[0] || eligibleClients[0]?.client_id || "VIRTUAL"),
                is_virtual: isAllVirtual,
            }
        ]);
    };

    const handleRemoveLeg = (index) => {
        if (legs.length <= 1) {
            toast.error("A basket must have at least 1 leg.");
            return;
        }
        setLegs((prev) => prev.filter((_, i) => i !== index));
    };

    const handleLegChange = (index, field, value) => {
        setLegs((prev) => {
            const next = [...prev];
            const updated = { ...next[index], [field]: value };

            if (["exchange", "instrument", "symbolType", "expiryMonth", "expiryYear", "strike"].includes(field)) {
                updated.symbol = buildSymbol({
                    exchange: updated.exchange,
                    instrument: updated.instrument,
                    symbolType: updated.symbolType,
                    expiryMonth: updated.expiryMonth,
                    expiryYear: updated.expiryYear,
                    strike: updated.strike,
                });
            }

            if (field === "is_virtual") {
                if (value) {
                    updated.broker = "virtual";
                    updated.client_id = "VIRTUAL";
                } else if (updated.broker === "virtual" && !isAllVirtual) {
                    updated.broker = "fyers";
                    updated.client_id = selectedClients[0] || eligibleClients[0]?.client_id || "";
                }
            }

            next[index] = updated;
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (selectedClients.length === 0) {
            toast.error("Please select at least one client.");
            return;
        }

        if (legs.length === 0) {
            toast.error("Please add at least one leg to the basket.");
            return;
        }

        for (let i = 0; i < legs.length; i++) {
            const leg = legs[i];
            if (!leg.symbol.trim()) {
                toast.error(`Leg #${i + 1}: Symbol is required.`);
                return;
            }
            if (leg.symbolType !== "FUT" && (!leg.strike || isNaN(Number(leg.strike)))) {
                toast.error(`Leg #${i + 1}: Strike price is required for ${leg.symbolType} options.`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const payload = {
                strategy_type: strategyType,
                target_pnl: targetPnl !== "" ? Number(targetPnl) : null,
                stop_loss_pnl: stopLossPnl !== "" ? Number(stopLossPnl) : null,
                clients: selectedClients,
                is_all_virtual: isAllVirtual,
                legs: legs.map((leg) => {
                    const clientObj = eligibleClients.find(c => c.clientId === (leg.client_id || selectedClients[0]));
                    const legBroker = (isAllVirtual || leg.is_virtual)
                        ? "virtual"
                        : (leg.broker || clientObj?.broker || "zerodha");
                    const legClientId = (isAllVirtual || leg.is_virtual)
                        ? "VIRTUAL"
                        : (leg.client_id || selectedClients[0] || "VIRTUAL");

                    return {
                        symbol: leg.symbol.trim().toUpperCase(),
                        exchange: leg.exchange,
                        side: Number(leg.side),
                        qty: Number(leg.qty) || 1,
                        orderType: leg.orderType,
                        limitPrice: Number(leg.limitPrice) || 0,
                        broker: legBroker,
                        client_id: legClientId,
                        is_virtual: Boolean(isAllVirtual || leg.is_virtual),
                    };
                })
            };

            const res = await apiClient.post("rms-arb/place-basket", payload);
            if (res.data && (res.data.status === 200 || res.data.status === "success" || res.data.basket_id)) {
                toast.success(res.data.message || `Arbitrage Basket(s) placed successfully!`);
                if (onSuccess) onSuccess();
                onClose();
            } else {
                toast.error(res.data?.message || "Failed to place arbitrage basket.");
            }
        } catch (err) {
            console.error("Error submitting basket:", err);
            toast.error("Failed to place basket order.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto animate-fade-in">
            <div className="bg-[#1a1d29] border border-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col text-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-[#13151f]">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30">
                            <Zap className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-white tracking-wide flex items-center gap-2">
                                Place RMS-Arbitrage Basket
                                {isAllVirtual && (
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                        SIMULATION MODE
                                    </span>
                                )}
                            </h2>
                            <p className="text-xs text-gray-400">Place arbitrage baskets for one or multiple client accounts</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form - Side by Side 2 Column Layout */}
                <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-hidden p-6 flex flex-col lg:flex-row gap-6">

                    {/* Left Column: Strategy, Risk Rules, Virtual Mode & Basket Legs */}
                    <div className="lg:w-1/2 space-y-4 flex flex-col min-h-0 overflow-y-auto pr-2 sleek-scrollbar">

                        {/* Strategy & Risk Rules Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#11131c] p-4 rounded-xl border border-gray-800/80">
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1">
                                    Strategy Type
                                </label>
                                <select
                                    value={strategyType}
                                    onChange={(e) => setStrategyType(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                                >
                                    <option value="cross_exchange_arb">Cross Exchange Arb</option>
                                    <option value="inter_asset_arb">Inter Asset Arb</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-emerald-400 mb-1">
                                    Target PnL (₹)
                                </label>
                                <input
                                    type="number"
                                    placeholder="e.g. 20000"
                                    value={targetPnl}
                                    onChange={(e) => setTargetPnl(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-red-400 mb-1">
                                    Stop Loss (₹)
                                </label>
                                <input
                                    type="number"
                                    placeholder="e.g. -10000"
                                    value={stopLossPnl}
                                    onChange={(e) => setStopLossPnl(e.target.value)}
                                    className="w-full bg-[#1a1d29] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-red-500"
                                />
                            </div>
                        </div>

                        {/* Simulation / Virtual Mode Master Switch */}
                        <div className={`p-3 rounded-xl border transition-all ${isAllVirtual
                                ? "bg-amber-950/20 border-amber-500/40 text-amber-300"
                                : "bg-[#11131c] border-gray-800/80 text-gray-300"
                            }`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {isAllVirtual ? (
                                        <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
                                    ) : (
                                        <ShieldCheck className="w-5 h-5 text-gray-400 shrink-0" />
                                    )}
                                    <div>
                                        <div className="text-xs font-bold text-white flex items-center gap-2">
                                            Virtual Simulation Mode
                                            {isAllVirtual && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 font-semibold">
                                                    VIRTUAL EXECS ONLY
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[11px] text-gray-400">
                                            Executes legs virtually for live MTM/RMS testing without placing broker orders.
                                        </p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-2">
                                    <input
                                        type="checkbox"
                                        checked={isAllVirtual}
                                        onChange={(e) => setIsAllVirtual(e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                                </label>
                            </div>
                        </div>

                        {/* Basket Legs Section */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300">
                                    Basket Legs ({legs.length})
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleAddLeg}
                                    className="flex items-center gap-1 px-3 py-1 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-bold hover:bg-blue-600/30 transition-all"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add Leg
                                </button>
                            </div>

                            <div className="space-y-3">
                                {legs.map((leg, idx) => {
                                    const isLegVirtual = isAllVirtual || leg.is_virtual;
                                    const isOption = leg.symbolType === "CE" || leg.symbolType === "PE";

                                    return (
                                        <div
                                            key={idx}
                                            className="bg-[#11131c] border border-gray-800/80 rounded-xl p-3 space-y-2.5 relative group"
                                        >
                                            {/* Leg Header */}
                                            <div className="flex items-center justify-between border-b border-gray-800/60 pb-2">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="w-5 h-5 rounded-full bg-gray-800 text-gray-300 text-xs flex items-center justify-center font-bold">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="text-xs font-semibold text-gray-400">Leg #{idx + 1}</span>
                                                    {isLegVirtual ? (
                                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                                            VIRTUAL
                                                        </span>
                                                    ) : (
                                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                                            REAL
                                                        </span>
                                                    )}
                                                    {/* Auto-generated Symbol Tag */}
                                                    <div className="ml-1 px-2 py-0.5 bg-gray-900 border border-gray-700/80 rounded text-xs font-mono font-bold text-amber-300">
                                                        {leg.symbol || "N/A"}
                                                        <LegLtpDisplay symbol={leg.symbol} />
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    {!isAllVirtual && (
                                                        <label className="flex items-center gap-1 text-[11px] text-gray-400 cursor-pointer select-none">
                                                            <input
                                                                type="checkbox"
                                                                checked={leg.is_virtual}
                                                                onChange={(e) => handleLegChange(idx, "is_virtual", e.target.checked)}
                                                                className="rounded bg-gray-800 border-gray-700 text-blue-500 focus:ring-0"
                                                            />
                                                            <span>Virtual</span>
                                                        </label>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveLeg(idx)}
                                                        className="text-gray-500 hover:text-red-400 p-1 transition-colors"
                                                        title="Remove Leg"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Symbol Controls */}
                                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Exchange</label>
                                                    <select
                                                        value={leg.exchange}
                                                        onChange={(e) => handleLegChange(idx, "exchange", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white font-semibold focus:border-blue-500"
                                                    >
                                                        {EXCHANGES.map((ex) => (
                                                            <option key={ex} value={ex}>{ex}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Instrument</label>
                                                    <select
                                                        value={leg.instrument}
                                                        onChange={(e) => handleLegChange(idx, "instrument", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white font-semibold focus:border-blue-500"
                                                    >
                                                        {INSTRUMENTS.map((inst) => (
                                                            <option key={inst} value={inst}>{inst}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Type</label>
                                                    <select
                                                        value={leg.symbolType}
                                                        onChange={(e) => handleLegChange(idx, "symbolType", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white font-semibold focus:border-blue-500"
                                                    >
                                                        {SYMBOL_TYPES.map((st) => (
                                                            <option key={st.value} value={st.value}>{st.label}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Month</label>
                                                    <select
                                                        value={leg.expiryMonth}
                                                        onChange={(e) => handleLegChange(idx, "expiryMonth", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white font-semibold focus:border-blue-500"
                                                    >
                                                        {MONTHS.map((m) => (
                                                            <option key={m.name} value={m.name}>
                                                                {m.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Year</label>
                                                    <select
                                                        value={leg.expiryYear}
                                                        onChange={(e) => handleLegChange(idx, "expiryYear", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white font-semibold focus:border-blue-500"
                                                    >
                                                        {YEARS.map((yr) => (
                                                            <option key={yr} value={yr}>20{yr}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Strike</label>
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        placeholder={isOption ? "Strike" : "N/A"}
                                                        disabled={!isOption}
                                                        value={isOption ? leg.strike : ""}
                                                        onChange={(e) => handleLegChange(idx, "strike", e.target.value)}
                                                        className={`w-full bg-[#1a1d29] border rounded px-1.5 py-1 text-xs text-white font-semibold focus:border-blue-500 ${!isOption ? "opacity-40 cursor-not-allowed border-gray-800" : "border-gray-700"
                                                            }`}
                                                    />
                                                </div>
                                            </div>

                                            {/* Execution Parameters */}
                                            <div className="grid grid-cols-4 gap-2 pt-1 border-t border-gray-800/40">
                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Side</label>
                                                    <select
                                                        value={leg.side}
                                                        onChange={(e) => handleLegChange(idx, "side", e.target.value)}
                                                        className={`w-full border rounded px-1.5 py-1 text-xs font-bold ${leg.side === "1"
                                                                ? "bg-blue-500/20 text-blue-400 border-blue-500/40"
                                                                : "bg-red-500/20 text-red-400 border-red-500/40"
                                                            }`}
                                                    >
                                                        <option value="1">BUY</option>
                                                        <option value="-1">SELL</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Lots</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={leg.qty}
                                                        onChange={(e) => handleLegChange(idx, "qty", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white font-semibold"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Limit Price</label>
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        placeholder="0 = MKT"
                                                        value={leg.limitPrice}
                                                        onChange={(e) => handleLegChange(idx, "limitPrice", e.target.value)}
                                                        className="w-full bg-[#1a1d29] border border-gray-700 rounded px-1.5 py-1 text-xs text-white"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Mode</label>
                                                    <div className="w-full bg-gray-800/60 border border-gray-700/60 rounded px-1.5 py-1 text-[10px] text-gray-300 font-mono truncate">
                                                        {isLegVirtual ? "VIRTUAL" : "PER-CLIENT"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: PledgePlus-Style Client Selection Panel */}
                    <div className="lg:w-1/2 flex flex-col min-h-0 overflow-hidden space-y-3">
                        <div className="flex items-center justify-between border-b border-indigo-900/30 pb-2">
                            <h3 className="text-indigo-400 font-bold text-sm uppercase tracking-wider flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span>Target Clients ({selectedClients.length} Selected)</span>
                            </h3>
                        </div>

                        {/* Search Bar & Broker Filter Dropdown */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search client by ID, name, label..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-[#0f1116] border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                                />
                            </div>
                            <select
                                value={brokerFilter}
                                onChange={(e) => setBrokerFilter(e.target.value)}
                                className="bg-[#0f1116] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 outline-none cursor-pointer focus:border-indigo-500"
                            >
                                <option value="ALL">All Brokers</option>
                                <option value="FYERS">Fyers</option>
                                <option value="ZERODHA">Zerodha</option>
                                <option value="DHAN_XTS">Dhan XTS</option>
                                <option value="DHAN_HQ">Dhan HQ</option>
                                <option value="NUBRA">Nubra</option>
                            </select>
                        </div>

                        {/* Quick Group / Label Badges */}
                        {uniqueLabels.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {uniqueLabels.map(label => {
                                    const labelClients = filteredClients.filter(c => c.label === label);
                                    if (labelClients.length === 0) return null;

                                    const selectedCount = labelClients.filter(c => selectedClients.includes(c.clientId)).length;
                                    const isAllSelected = selectedCount === labelClients.length && labelClients.length > 0;

                                    return (
                                        <button
                                            key={label}
                                            type="button"
                                            onClick={() => toggleLabelSelection(label)}
                                            className={`px-2 py-1 rounded text-[10px] font-bold uppercase transition-all border flex items-center gap-1.5 ${isAllSelected
                                                    ? 'bg-indigo-600 border-indigo-500 text-white'
                                                    : selectedCount > 0
                                                        ? 'bg-indigo-900/40 border-indigo-700 text-indigo-200'
                                                        : 'bg-[#14161f] border-gray-700 text-gray-500 hover:border-gray-500'
                                                }`}
                                        >
                                            <span>{label}</span>
                                            <span className={`px-1 rounded text-[9px] ${isAllSelected ? 'bg-indigo-800 text-white' : 'bg-gray-800 text-gray-400'}`}>
                                                {selectedCount}/{labelClients.length}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Client Checkbox List Container */}
                        <div className="flex-1 min-h-0 bg-[#0f1116] rounded-xl border border-gray-800 overflow-hidden flex flex-col">
                            <div className="bg-[#14161f] px-3.5 py-2 flex items-center justify-between border-b border-gray-800">
                                <button
                                    type="button"
                                    onClick={toggleSelectAll}
                                    className="text-[10px] text-indigo-400 font-bold hover:text-indigo-300 uppercase tracking-wider"
                                >
                                    {filteredClients.length > 0 && filteredClients.every(c => selectedClients.includes(c.clientId))
                                        ? "Deselect All Visible"
                                        : "Select All Visible"}
                                </button>
                                <span className="text-[10px] text-gray-500 uppercase font-mono">
                                    {filteredClients.length} clients eligible
                                </span>
                            </div>

                            <div className="flex-1 overflow-y-auto sleek-scrollbar divide-y divide-gray-800/40">
                                {filteredClients.map((client) => {
                                    const cid = client.clientId;
                                    const isSelected = selectedClients.includes(cid);

                                    return (
                                        <div
                                            key={cid}
                                            onClick={() => toggleClientSelection(cid)}
                                            className={`px-3.5 py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-indigo-500/5 transition-colors ${isSelected ? 'bg-indigo-500/10' : ''
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isSelected ? 'bg-indigo-600 border-indigo-500' : 'border-gray-600'
                                                    }`}>
                                                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                                </div>

                                                <div>
                                                    <p className="text-white text-xs font-semibold">{client.name}</p>
                                                    <p className="text-gray-400 text-[11px] font-mono">
                                                        {cid} {client.broker ? `| ${client.broker.toUpperCase()}` : ""}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {filteredClients.length === 0 && (
                                    <div className="p-8 text-center">
                                        <Filter className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                                        <p className="text-gray-500 text-xs">No matching target clients found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-3.5 border-t border-gray-800 bg-[#13151f]">
                    <div className="text-xs text-gray-400">
                        Placing <span className="text-white font-bold">{selectedClients.length}</span> basket(s)
                        {isAllVirtual && <span className="text-amber-400 font-semibold ml-1">(100% Virtual Simulation)</span>}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs font-semibold transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || selectedClients.length === 0}
                            className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Zap className="w-4 h-4" />
                            <span>
                                {submitting
                                    ? "Placing Baskets..."
                                    : `Place Baskets for ${selectedClients.length} Client(s)`}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(RmsArbOrderModal);

